import React, { useState, useEffect } from 'react';
import { Menu, X, MessageSquareMore, Github, Linkedin, Mail, Sun, Moon } from 'lucide-react';
import { Button } from './ui/button';
import { usePortfolio } from '../context/PortfolioContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';

const Header = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { portfolioData } = usePortfolio();
  const hero = portfolioData?.hero;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const navLinks = [
    { name: 'Accueil', id: 'hero' },
    { name: 'À propos', id: 'about' },
    { name: 'Projets', id: 'projects' },
    { name: 'Compétences', id: 'skills' },
    { name: 'Expérience', id: 'experience' },
    { name: 'Contact', id: 'contact' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b border-gray-200 dark:border-[#1f2937]
         ${isScrolled 
           ? 'bg-white/95 dark:bg-[#0a0e1a]/95 backdrop-blur-md shadow-lg' 
           : 'bg-white/80 dark:bg-[#0a0e1a]/80 backdrop-blur-sm'}`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <button
            onClick={() => navigate("/")}
            className="text-xl font-bold text-gray-900 dark:text-white hover:text-emerald-400 transition-colors"
          >
            <img src="/images/logo.png" alt="Logo" className="h-10 w-10" />
          </button>

          <nav className="hidden items-center space-x-4 lg:flex lg:space-x-8">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="text-gray-700 dark:text-gray-300 hover:text-emerald-400 dark:hover:text-emerald-300 transition-all duration-300 text-sm font-medium hover:scale-105"
              >
                {link.name}
              </button>
            ))}
          </nav>

          <div className="hidden items-center space-x-3 lg:flex lg:space-x-4">
            <a
              href={ hero?.whatsapp || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-700 dark:text-gray-300 hover:text-emerald-400 dark:hover:text-emerald-300 transition-all duration-300 hover:scale-110"
            >
              <MessageSquareMore className="w-5 h-5" />
            </a>
            <a
              href={hero?.github || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-700 dark:text-gray-300 hover:text-emerald-400 dark:hover:text-emerald-300 transition-all duration-300 hover:scale-110"
            >
              <Github className="w-5 h-5" />
            </a>
            <a
              href={hero?.linkedin || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-700 dark:text-gray-300 hover:text-emerald-400 dark:hover:text-emerald-300 transition-all duration-300 hover:scale-110"
            >
              <Linkedin className="w-5 h-5" />
            </a>
            <a
              href={`mailto:${hero?.email || ''}`}
              className="text-gray-700 dark:text-gray-300 hover:text-emerald-400 dark:hover:text-emerald-300 transition-all duration-300 hover:scale-110"
            >
              <Mail className="w-5 h-5" />
            </a>
            <button
              onClick={toggleTheme}
              className="text-gray-700 dark:text-gray-300 hover:text-emerald-400 dark:hover:text-emerald-300 transition-all duration-300 p-2 rounded-full hover:bg-emerald-500/10 dark:hover:bg-emerald-500/20 hover:scale-110"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-gray-900 transition-colors hover:text-emerald-400 dark:text-white lg:hidden"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="border-t border-gray-200 bg-white backdrop-blur-md dark:border-[#1f2937] dark:bg-[#0f1419] lg:hidden">
          <nav className="container mx-auto px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="block w-full text-left text-gray-700 dark:text-gray-300 hover:text-emerald-400 dark:hover:text-emerald-300 transition-all duration-300 py-2 hover:pl-2"
              >
                {link.name}
              </button>
            ))}
            <div className="flex items-center space-x-4 pt-4 border-t border-gray-200 dark:border-[#1f2937]">
              <a
                href={ hero?.whatsapp || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-700 dark:text-gray-300 hover:text-emerald-400 dark:hover:text-emerald-300 transition-all duration-300 hover:scale-110"
              >
                <MessageSquareMore className="w-5 h-5" />
              </a>
              <a
                href={hero?.github || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-700 dark:text-gray-300 hover:text-emerald-400 dark:hover:text-emerald-300 transition-all duration-300 hover:scale-110"
              >
                <Github className="w-5 h-5" />
              </a>
              <a
                href={hero?.linkedin || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-700 dark:text-gray-300 hover:text-emerald-400 dark:hover:text-emerald-300 transition-all duration-300 hover:scale-110"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href={`mailto:${hero?.email || ''}`}
                className="text-gray-700 dark:text-gray-300 hover:text-emerald-400 dark:hover:text-emerald-300 transition-all duration-300 hover:scale-110"
              >
                <Mail className="w-5 h-5" />
              </a>
              <button
                onClick={toggleTheme}
                className="text-gray-700 dark:text-gray-300 hover:text-emerald-400 dark:hover:text-emerald-300 transition-all duration-300 p-2 rounded-lg hover:bg-emerald-500/10 dark:hover:bg-emerald-500/20 hover:scale-110"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;